import { Vec3 } from '@/core/math/Vec3';
import { clamp, clamp01 } from '@/core/math/scalar';

export type ActionId =
  | 'pitchUp'
  | 'pitchDown'
  | 'yawLeft'
  | 'yawRight'
  | 'rollLeft'
  | 'rollRight'
  | 'translateForward'
  | 'translateBack'
  | 'translateLeft'
  | 'translateRight'
  | 'translateUp'
  | 'translateDown'
  | 'throttleUp'
  | 'throttleDown'
  | 'throttleMax'
  | 'throttleCut'
  | 'toggleMainEngine'
  | 'toggleAtmosphericEngines'
  | 'toggleLandingThrusters'
  | 'toggleFieldDrive'
  | 'toggleRotationDamping'
  | 'alignPrograde'
  | 'alignRetrograde'
  | 'alignRadialOut'
  | 'alignRadialIn'
  | 'alignNormal'
  | 'alignTarget'
  | 'alignSurfaceUp'
  | 'holdAltitude'
  | 'hover'
  | 'freeLook'
  | 'warpUp'
  | 'warpDown'
  | 'warpReset'
  | 'macroLaunch'
  | 'macroLand'
  | 'macroCircularise'
  | 'macroDock'
  | 'macroTransfer'
  | 'macroAbort'
  | 'jump'
  | 'sprint'
  | 'crouch'
  | 'interact'
  | 'toggleView'
  | 'boardShip'
  | 'toggleMap'
  | 'toggleBuild'
  | 'toggleSettings'
  | 'quickSave'
  | 'pause'
  | 'boost'
  | 'toggleFreeFlight'
  | 'freeFlightFaster'
  | 'freeFlightSlower'
  | 'toggleAudio';

export type Bindings = Record<ActionId, string[]>;

/**
 * Flight is mouse-aimed and WASD-driven, the way a space game is expected to
 * be, rather than the aircraft-style stick-on-the-keyboard layout it started
 * with. The mouse commands rotation and the letters command translation, which
 * keeps the two things a pilot thinks about on separate hands.
 *
 * The keyboard attitude keys still exist on the arrow keys, because a mouse is
 * a terrible way to hold a precise attitude and docking needs one.
 */
export const DEFAULT_BINDINGS: Bindings = {
  pitchUp: ['ArrowDown'],
  pitchDown: ['ArrowUp'],
  yawLeft: ['ArrowLeft'],
  yawRight: ['ArrowRight'],
  rollLeft: ['KeyQ'],
  rollRight: ['KeyE'],
  translateForward: ['KeyW'],
  translateBack: ['KeyS'],
  translateLeft: ['KeyA'],
  translateRight: ['KeyD'],
  translateUp: ['Space'],
  translateDown: ['ControlLeft'],
  boost: ['ShiftLeft'],
  throttleUp: ['BracketRight'],
  throttleDown: ['BracketLeft'],
  throttleMax: ['KeyZ'],
  throttleCut: ['KeyX'],
  toggleMainEngine: ['Digit1'],
  toggleAtmosphericEngines: ['Digit2'],
  toggleLandingThrusters: ['Digit3'],
  toggleFieldDrive: ['KeyF'],
  toggleRotationDamping: ['KeyR'],
  alignPrograde: ['Numpad8'],
  alignRetrograde: ['Numpad2'],
  alignRadialOut: ['Numpad9'],
  alignRadialIn: ['Numpad7'],
  alignNormal: ['Numpad4'],
  alignTarget: ['Numpad5', 'KeyT'],
  alignSurfaceUp: ['Numpad6'],
  holdAltitude: ['KeyH'],
  hover: ['KeyG'],
  freeLook: ['AltLeft'],
  warpUp: ['Period'],
  warpDown: ['Comma'],
  warpReset: ['Slash'],
  macroLaunch: ['F1'],
  macroLand: ['F2'],
  macroCircularise: ['F3'],
  macroDock: ['F4'],
  // F5 is already quickSave (and the browser's own reload key), so the transfer
  // macro takes the next free function key rather than double-binding it.
  macroTransfer: ['F6'],
  macroAbort: ['Backspace'],
  jump: ['Space'],
  sprint: ['ShiftLeft'],
  crouch: ['ControlLeft'],
  toggleFreeFlight: ['F8'],
  freeFlightFaster: ['Equal'],
  freeFlightSlower: ['Minus'],
  toggleAudio: ['KeyO'],
  interact: ['KeyE'],
  toggleView: ['KeyV'],
  boardShip: ['KeyB'],
  toggleMap: ['KeyM'],
  toggleBuild: ['KeyU'],
  toggleSettings: ['F9'],
  quickSave: ['F5'],
  pause: ['Escape'],
};

export interface AxisState {
  /** Body-axis rotation demand, -1..1 per axis. */
  rotation: Vec3;
  /** Body-axis translation demand, -1..1 per axis. */
  translation: Vec3;
  /** Manual cruise throttle, 0..1. Set by the throttle keys and held. */
  throttle: number;
  /**
   * Momentary forward thrust demand from the W key, 0..1.
   *
   * Separate from the cruise throttle so the two do not fight: hold W for a
   * burst, set the cruise throttle for a long burn, and the vessel obeys
   * whichever is asking for more. Ramped rather than binary because an engine
   * that goes from nothing to full thrust in one frame makes precise flying
   * near terrain impossible.
   */
  thrust: number;
  /**
   * Mouse look delta this frame, radians. Positive yaw turns right, positive
   * pitch looks up. Used directly by the on-foot camera.
   */
  lookYaw: number;
  lookPitch: number;
  /** Mouse wheel travel this frame, in notches. Positive is scroll-up. */
  wheel: number;
  /**
   * Mouse-derived attitude stick, -1..1 per axis, x = pitch, y = yaw.
   *
   * The mouse steers the vessel by deflecting a virtual stick rather than by
   * setting an orientation. Moving the mouse deflects it, releasing lets it
   * decay back to centre, and the flight computer turns the deflection into a
   * rate demand and then a torque. Nothing here writes an orientation, which is
   * the whole point: a mouse that assigned attitude directly would let a pilot
   * out-turn their own reaction wheels.
   */
  mouseStick: Vec3;
  /**
   * Camera-only look-around offset, radians, held while `freeLook` is down.
   * Rotates the cockpit/chase view without touching `mouseStick`, so looking
   * around never feeds a rotation demand to the flight computer.
   */
  freeLookYaw: number;
  freeLookPitch: number;
}

/**
 * Keyboard, mouse and gamepad input.
 *
 * Two things worth noting:
 *
 *  - Actions are separated from keys, so rebinding is a data change and the
 *    same physical key can mean different things in different contexts (E is
 *    "roll right" in a cockpit and "interact" on foot).
 *
 *  - Rotation and translation are reported as *analogue axes*, not as events.
 *    The flight computer consumes them as continuous demands, which is what lets
 *    the same code path serve a keyboard, a gamepad stick and an autopilot.
 */
export class InputManager {
  private pressed = new Set<string>();
  private justPressed = new Set<string>();
  private bindings: Bindings;
  private reverseMap = new Map<string, ActionId[]>();

  readonly axes: AxisState = {
    rotation: new Vec3(),
    translation: new Vec3(),
    throttle: 0,
    thrust: 0,
    lookYaw: 0,
    lookPitch: 0,
    wheel: 0,
    mouseStick: new Vec3(),
    freeLookYaw: 0,
    freeLookPitch: 0,
  };

  /** Mouse sensitivity, radians per pixel. */
  mouseSensitivity = 0.0022;
  /** Virtual stick deflection per pixel of mouse travel. */
  mouseStickGain = 0.016;
  /**
   * Time constant for the virtual stick returning to centre, seconds.
   *
   * Short enough that the vessel stops turning promptly when the mouse stops,
   * long enough that a steady drag holds a steady turn rate.
   */
  mouseStickDecay = 0.12;
  /** Furthest the free-look offset can turn from centre, radians. */
  freeLookYawLimit = 2.6;
  freeLookPitchLimit = 1.45;
  /** Time constant for the free-look offset snapping back to centre on release, seconds. */
  freeLookRecenter = 0.15;
  /** Invert the pitch axis. */
  invertPitch = false;
  /** Gamepad dead zone. */
  deadZone = 0.12;
  /** Forward thrust demand without the boost key, 0..1. */
  cruiseThrust = 0.45;
  /** Engine spool rates, fraction per second. */
  thrustSpoolUp = 2.4;
  thrustSpoolDown = 4;

  /** True while the pointer is locked to the canvas. */
  pointerLocked = false;
  /** Suppress game input while a UI panel has focus. */
  uiFocus = false;

  /**
   * On-screen (touch) input.
   *
   * A phone has no keyboard, no mouse and no pointer lock, so the touch UI
   * needs a way to state the same demands a key or a stick would. It states
   * them here rather than synthesising `KeyboardEvent`s: the held/one-shot
   * sets below join `isDown`/`wasPressed` at the same level the physical keys
   * do, so every consumer — the flight computer, the on-foot controller, the
   * free camera, `Game.applyActions` — keeps reading exactly one API and none
   * of them has to know a finger is driving.
   *
   * The two Vec3s are analogue, in the same body frame as `axes.rotation` and
   * `axes.translation` (+X port, +Y dorsal, +Z nose), because a thumbstick has
   * a magnitude and quantising it to on/off throws away the only advantage it
   * has over a key.
   */
  private virtualDown = new Set<ActionId>();
  private virtualJustPressed = new Set<ActionId>();
  private readonly virtualRotation = new Vec3();
  private readonly virtualTranslation = new Vec3();

  private accumulatedYaw = 0;
  private accumulatedPitch = 0;
  private stickPitch = 0;
  private stickYaw = 0;
  /**
   * This frame's raw, un-gained pointer motion feeding the stick, in device
   * pixels. Kept separate from `stickYaw`/`stickPitch` themselves -- see the
   * weighting comment in `update()` for why the two can't be combined until
   * `dt` is known.
   */
  private stickRawYaw = 0;
  private stickRawPitch = 0;
  private freeLookYaw = 0;
  private freeLookPitch = 0;
  private accumulatedWheel = 0;
  private gamepadIndex: number | null = null;
  private listeners: (() => void)[] = [];

  constructor(
    private readonly element: HTMLElement,
    bindings: Bindings = DEFAULT_BINDINGS,
  ) {
    this.bindings = bindings;
    this.rebuildReverseMap();
    this.attach();
  }

  private rebuildReverseMap(): void {
    this.reverseMap.clear();
    for (const [action, keys] of Object.entries(this.bindings) as [ActionId, string[]][]) {
      for (const key of keys) {
        const list = this.reverseMap.get(key) ?? [];
        list.push(action);
        this.reverseMap.set(key, list);
      }
    }
  }

  setBindings(bindings: Bindings): void {
    this.bindings = bindings;
    this.rebuildReverseMap();
  }

  getBindings(): Bindings {
    return this.bindings;
  }

  private attach(): void {
    // Tests construct an InputManager to exercise the axis composition, which
    // is pure arithmetic and needs no DOM. There is nothing to listen to there
    // and nothing to clean up, so the whole listener block is skipped rather
    // than each caller having to fake a window.
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const onKeyDown = (e: KeyboardEvent): void => {
      if (this.uiFocus && e.code !== 'Escape' && e.code !== 'Tab') return;
      if (!this.pressed.has(e.code)) this.justPressed.add(e.code);
      this.pressed.add(e.code);
      // Prevent the browser from stealing keys the game uses.
      if (this.reverseMap.has(e.code) && e.code !== 'F5' && e.code !== 'F12') e.preventDefault();
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      this.pressed.delete(e.code);
    };
    const onBlur = (): void => {
      // Losing focus with keys held would otherwise leave a stuck throttle.
      this.pressed.clear();
      this.justPressed.clear();
      // A finger held on an on-screen control when the tab goes away never
      // gets its `pointerup`, so release those too.
      this.clearVirtualInput();
    };
    const onMouseMove = (e: MouseEvent): void => {
      if (!this.pointerLocked || this.uiFocus) return;
      this.applyLookDelta(e.movementX, e.movementY);
    };
    const onWheel = (e: WheelEvent): void => {
      if (this.uiFocus) return;
      // Browsers report wheel travel in pixels, lines or pages depending on the
      // device. Normalising to notches keeps a trackpad and a mouse wheel
      // roughly comparable.
      const perNotch = e.deltaMode === 1 ? 3 : e.deltaMode === 2 ? 300 : 100;
      this.accumulatedWheel -= e.deltaY / perNotch;
      if (this.pointerLocked) e.preventDefault();
    };
    const onPointerLockChange = (): void => {
      this.pointerLocked = document.pointerLockElement === this.element;
    };
    const onGamepadConnected = (e: GamepadEvent): void => {
      this.gamepadIndex = e.gamepad.index;
    };
    const onGamepadDisconnected = (e: GamepadEvent): void => {
      if (this.gamepadIndex === e.gamepad.index) this.gamepadIndex = null;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('pointerlockchange', onPointerLockChange);
    window.addEventListener('gamepadconnected', onGamepadConnected);
    window.addEventListener('gamepaddisconnected', onGamepadDisconnected);

    this.listeners.push(
      () => window.removeEventListener('keydown', onKeyDown),
      () => window.removeEventListener('keyup', onKeyUp),
      () => window.removeEventListener('blur', onBlur),
      () => window.removeEventListener('mousemove', onMouseMove),
      () => window.removeEventListener('wheel', onWheel),
      () => document.removeEventListener('pointerlockchange', onPointerLockChange),
      () => window.removeEventListener('gamepadconnected', onGamepadConnected),
      () => window.removeEventListener('gamepaddisconnected', onGamepadDisconnected),
    );
  }

  /**
   * Feeds a pointer movement in device pixels to the look/attitude channels.
   *
   * Shared by the locked mouse and by a finger dragging the viewport, so a
   * touch drag steers the vessel through the same virtual stick a mouse does
   * (see `axes.mouseStick`) rather than through a second, subtly different
   * path that would need its own tuning and its own sign bugs.
   */
  applyLookDelta(dx: number, dy: number): void {
    if (this.uiFocus) return;
    const dyRaw = this.invertPitch ? dy : -dy;
    if (this.isDown('freeLook')) {
      // Diverted here instead of into the flight stick: this is the whole
      // point of free look, not an incidental side effect.
      this.freeLookYaw = clamp(
        this.freeLookYaw + dx * this.mouseSensitivity,
        -this.freeLookYawLimit,
        this.freeLookYawLimit,
      );
      this.freeLookPitch = clamp(
        this.freeLookPitch + dyRaw * this.mouseSensitivity,
        -this.freeLookPitchLimit,
        this.freeLookPitchLimit,
      );
      return;
    }
    // Moving the mouse right turns right. The obvious sign is the correct
    // one; negating it here is what made walking steer backwards.
    this.accumulatedYaw += dx * this.mouseSensitivity;
    this.accumulatedPitch += dyRaw * this.mouseSensitivity;
    // Raw pixels only here -- gain and decay are applied once per frame in
    // `update()`, where `dt` is known. See its comment for why.
    this.stickRawYaw += dx;
    this.stickRawPitch += dyRaw;
  }

  /** Holds or releases an action from the on-screen controls. */
  setVirtualAction(action: ActionId, down: boolean): void {
    if (down) {
      if (!this.virtualDown.has(action)) this.virtualJustPressed.add(action);
      this.virtualDown.add(action);
    } else {
      this.virtualDown.delete(action);
    }
  }

  /**
   * Fires an action once, for a control that is a tap rather than a hold.
   *
   * Deliberately not routed through `setVirtualAction(a, true)` followed by a
   * release: the release would have to wait for a frame boundary the UI cannot
   * see, and a tap that spans no frame at all would otherwise be swallowed.
   * `wasPressed` reads this set until `endFrame` clears it, so a tap always
   * lands on exactly one frame however briefly the finger was down.
   */
  pressVirtualAction(action: ActionId): void {
    this.virtualJustPressed.add(action);
  }

  /** Body-axis rotation demand from an on-screen stick, -1..1 per axis. */
  setVirtualRotation(x: number, y: number, z: number): void {
    this.virtualRotation.set(clamp(x, -1, 1), clamp(y, -1, 1), clamp(z, -1, 1));
  }

  /** Body-axis translation demand from an on-screen stick, -1..1 per axis. */
  setVirtualTranslation(x: number, y: number, z: number): void {
    this.virtualTranslation.set(clamp(x, -1, 1), clamp(y, -1, 1), clamp(z, -1, 1));
  }

  /** Sets the held cruise throttle directly, for an on-screen throttle slider. */
  setThrottle(value: number): void {
    this.axes.throttle = clamp01(value);
  }

  /**
   * Drops every on-screen demand.
   *
   * A finger has no `pointerup` guarantee — backgrounding the tab, a phone
   * call or an element unmounting mid-drag all end the gesture without one —
   * so anything that can interrupt a touch has to release the controls
   * explicitly or the vessel keeps thrusting.
   */
  clearVirtualInput(): void {
    this.virtualDown.clear();
    this.virtualJustPressed.clear();
    this.virtualRotation.set(0, 0, 0);
    this.virtualTranslation.set(0, 0, 0);
  }

  requestPointerLock(): void {
    void this.element.requestPointerLock?.();
  }

  exitPointerLock(): void {
    if (typeof document !== 'undefined' && document.pointerLockElement) document.exitPointerLock();
  }

  isDown(action: ActionId): boolean {
    if (this.uiFocus) return false;
    if (this.virtualDown.has(action)) return true;
    return this.bindings[action].some((key) => this.pressed.has(key));
  }

  wasPressed(action: ActionId): boolean {
    if (this.virtualJustPressed.has(action)) return true;
    return this.bindings[action].some((key) => this.justPressed.has(key));
  }

  /** Rebuilds the analogue axes. Call once per frame before the simulation. */
  update(dt: number): void {
    const axis = (positive: ActionId, negative: ActionId): number =>
      (this.isDown(positive) ? 1 : 0) - (this.isDown(negative) ? 1 : 0);

    // Signs follow the body frame (+X port, +Y dorsal, +Z nose) under the
    // right-hand rule, so a positive demand about an axis is the rotation that
    // axis actually produces:
    //   +X rotates the nose down, +Y rotates it left, +Z rolls right.
    // Naming an action "pitch up" and handing it +X would quietly fly the
    // vessel the other way, which is why the mapping is spelled out here rather
    // than left to match by coincidence.
    this.axes.rotation.set(
      axis('pitchDown', 'pitchUp'),
      axis('yawLeft', 'yawRight'),
      axis('rollRight', 'rollLeft'),
    );
    // Body axes again: +X is port, so "strafe left" is the positive demand.
    this.axes.translation.set(
      axis('translateLeft', 'translateRight'),
      axis('translateUp', 'translateDown'),
      axis('translateForward', 'translateBack'),
    );

    // On-screen sticks add to the keys rather than replacing them, the same
    // way the gamepad below does, so a touch device with a keyboard attached
    // behaves like one device instead of two fighting ones.
    this.axes.rotation.set(
      clamp(this.axes.rotation.x + this.virtualRotation.x, -1, 1),
      clamp(this.axes.rotation.y + this.virtualRotation.y, -1, 1),
      clamp(this.axes.rotation.z + this.virtualRotation.z, -1, 1),
    );
    this.axes.translation.set(
      clamp(this.axes.translation.x + this.virtualTranslation.x, -1, 1),
      clamp(this.axes.translation.y + this.virtualTranslation.y, -1, 1),
      clamp(this.axes.translation.z + this.virtualTranslation.z, -1, 1),
    );

    if (this.isDown('throttleUp')) this.axes.throttle = clamp01(this.axes.throttle + dt * 0.65);
    if (this.isDown('throttleDown')) this.axes.throttle = clamp01(this.axes.throttle - dt * 0.65);
    if (this.wasPressed('throttleMax')) this.axes.throttle = 1;
    if (this.wasPressed('throttleCut')) this.axes.throttle = 0;

    // Cruise power on its own, full power with the boost key. Holding some
    // authority in reserve is what makes hovering and landing controllable.
    //
    // A stick pushed halfway forward asks for half of that, which is the
    // point of an analogue control; a key is all-or-nothing and still reads as
    // exactly 1, so the keyboard feel is unchanged.
    const forward = Math.max(this.isDown('translateForward') ? 1 : 0, Math.max(0, this.virtualTranslation.z));
    const thrustTarget = forward * (this.isDown('boost') ? 1 : this.cruiseThrust);
    const rate = thrustTarget > this.axes.thrust ? this.thrustSpoolUp : this.thrustSpoolDown;
    this.axes.thrust = clamp01(this.axes.thrust + clamp(thrustTarget - this.axes.thrust, -rate * dt, rate * dt));

    this.applyGamepad();

    this.axes.lookYaw = this.accumulatedYaw;
    this.axes.lookPitch = this.accumulatedPitch;
    this.axes.wheel = this.accumulatedWheel;
    this.accumulatedYaw = 0;
    this.accumulatedPitch = 0;
    this.accumulatedWheel = 0;

    // Free look snaps back to centre once the key is released, like a head
    // returning to face forward, rather than staying wherever it was left.
    if (!this.isDown('freeLook')) {
      const recenter = Math.exp(-dt / Math.max(1e-3, this.freeLookRecenter));
      this.freeLookYaw *= recenter;
      this.freeLookPitch *= recenter;
      if (Math.abs(this.freeLookYaw) < 1e-4) this.freeLookYaw = 0;
      if (Math.abs(this.freeLookPitch) < 1e-4) this.freeLookPitch = 0;
    }
    this.axes.freeLookYaw = this.freeLookYaw;
    this.axes.freeLookPitch = this.freeLookPitch;

    // The stick is a first-order lag: it decays toward centre with time
    // constant `mouseStickDecay` and is driven by however much the mouse
    // moved this frame. `applyLookDelta` only ever sees a pixel delta, not
    // when within the frame it arrived, so this frame's motion has to be
    // treated as a steady rate spread over the whole of `dt` rather than as
    // a lump added before decay runs — decaying a whole frame's worth of
    // *fresh* input by the same factor as the *old, already-stale* value
    // applies a slow frame's full decay to input that, on average, has only
    // been sitting there for half that long. At 8 fps that shrank the same
    // real mouse motion to well under half its 60 fps deflection — reported
    // as needing to drag the mouse slowly, in small steady increments,
    // rather than the usual quick flicks, for the ship or free-flight camera
    // to turn the amount the mouse actually moved.
    //
    // The exact solution for a first-order lag driven at constant rate `r`
    // over one step is `old*decay + r*tau*(1-decay)`; since this frame's
    // total pixel delta is `r*dt`, that becomes `raw*gain*tau*(1-decay)/dt`.
    // The weight converges to 1 as dt -> 0, recovering the old per-event
    // behaviour exactly at a normal framerate, and shrinks appropriately as
    // dt grows.
    const tau = Math.max(1e-3, this.mouseStickDecay);
    const decay = Math.exp(-dt / tau);
    const rateWeight = dt > 1e-6 ? (tau * (1 - decay)) / dt : 1;
    this.stickYaw = clamp(this.stickYaw * decay + this.stickRawYaw * this.mouseStickGain * rateWeight, -1, 1);
    this.stickPitch = clamp(this.stickPitch * decay + this.stickRawPitch * this.mouseStickGain * rateWeight, -1, 1);
    this.stickRawYaw = 0;
    this.stickRawPitch = 0;
    if (Math.abs(this.stickPitch) < 1e-4) this.stickPitch = 0;
    if (Math.abs(this.stickYaw) < 1e-4) this.stickYaw = 0;
    // Mouse up means nose up, mouse right means nose right; both are the
    // negative body-axis demand.
    this.axes.mouseStick.set(-this.stickPitch, -this.stickYaw, 0);
  }

  /** Clears one-shot state. Call at the very end of the frame. */
  endFrame(): void {
    this.justPressed.clear();
    this.virtualJustPressed.clear();
  }

  private applyGamepad(): void {
    if (this.gamepadIndex === null || typeof navigator.getGamepads !== 'function') return;
    const pad = navigator.getGamepads()[this.gamepadIndex];
    if (!pad) return;

    const dz = (v: number): number => {
      const a = Math.abs(v);
      return a <= this.deadZone ? 0 : Math.sign(v) * ((a - this.deadZone) / (1 - this.deadZone));
    };

    // Left stick: pitch and yaw. Right stick: translation. Triggers: roll.
    // Stick forward (negative axis) is nose down, which is +X; stick right is
    // nose right, which is -Y. Same body-axis convention as the keyboard.
    const pitch = dz(pad.axes[1] ?? 0);
    const yaw = dz(pad.axes[0] ?? 0);
    if (pitch !== 0) this.axes.rotation.x = clamp(this.axes.rotation.x - pitch, -1, 1);
    if (yaw !== 0) this.axes.rotation.y = clamp(this.axes.rotation.y - yaw, -1, 1);

    const strafe = dz(pad.axes[2] ?? 0);
    const lift = dz(pad.axes[3] ?? 0);
    if (strafe !== 0) this.axes.translation.x = clamp(this.axes.translation.x - strafe, -1, 1);
    if (lift !== 0) this.axes.translation.y = clamp(this.axes.translation.y - lift, -1, 1);

    const rollLeft = pad.buttons[4]?.value ?? 0;
    const rollRight = pad.buttons[5]?.value ?? 0;
    if (rollLeft || rollRight) {
      this.axes.rotation.z = clamp(this.axes.rotation.z - rollLeft + rollRight, -1, 1);
    }

    const trigger = pad.buttons[7]?.value ?? 0;
    if (trigger > 0.02) this.axes.throttle = trigger;
  }

  /** Largest control deflection this frame; used to detect macro override. */
  get inputMagnitude(): number {
    return Math.max(
      Math.abs(this.axes.rotation.x),
      Math.abs(this.axes.rotation.y),
      Math.abs(this.axes.rotation.z),
      Math.abs(this.axes.translation.x),
      Math.abs(this.axes.translation.y),
      Math.abs(this.axes.translation.z),
    );
  }

  dispose(): void {
    for (const off of this.listeners) off();
    this.listeners.length = 0;
    this.pressed.clear();
    this.clearVirtualInput();
  }
}
